import * as THREE from 'three';
import { createHealth, applyDamage, tickHealth, FLASH_DURATION, type Health } from '../combat/damage';
import type { AimTarget } from '../combat/autoaim';
import type { BlockPhysics } from '../world/collision';
import { EXPOSED_DAMAGE_MULTIPLIER, CHIP_DAMAGE_MULTIPLIER } from './boss';
import { CHARACTER_FORWARD_YAW_OFFSET } from '../core/assets';

/**
 * C08/Wave M — the Rat King's boss-tier fight (PLAN-CITY2 §5 M-1), built as
 * a SIBLING to `Boss` (Gridlock, ai/boss.ts), not a shared subclass/config
 * variant of it. Boss's own header comment already explains why a boss is
 * deliberately NOT `Enemy`; the same reasoning applies one level up here —
 * the Rat King needs a THREE-way rotating opener (melee / swarm-call /
 * red-charge, not Gridlock's two-way attack1/attack2 alternation) plus an
 * entirely separate sub-FSM for the charge (windup -> committed dash ->
 * slam -> stagger) that doesn't fit Boss's telegraph->attack->exposed shape
 * at all. Sharing that shape by adding branches to `Boss` would risk the
 * already-shipped, already-reviewed Gridlock fight for a structural
 * difference this large; duplicating the small amount of genuinely-shared
 * plumbing (Health/knockback/tint/death-fade, imported from
 * `combat/damage.ts` exactly like Boss does) is cheaper and safer than a
 * premature merge.
 *
 * PLAN-CITY2 §10: this class IS Wave M's "Rat King Enemy->Boss migration"
 * deliverable, but it is deliberately NOT wired into the live world this
 * session — the old roaming `Enemy` (kind `rat_king`, ai/archetypes.ts)
 * stays the live encounter until Wave D actually builds the basement den
 * (PLAN-CITY2 §4 D-1). Spawning a live `RatKingBoss` today, with no den to
 * gate it, would put a full boss fight in the open block — a direct
 * violation of PLAN-CITY2 §0.6 ("no boss or elite spawns in the open
 * block"), the exact law this whole plan exists to enforce. This class is
 * built complete and ready to drop in; the swap-over (retiring the old
 * roaming archetype, spawning this instead inside the den, wiring a real
 * `BossArena` for it) is Wave D/M's coordinated follow-up once the den
 * geometry exists — see PLAN-CITY2 §10's module-ownership table.
 */
export type RatKingState =
  | 'idle'
  | 'telegraph1'
  | 'attack1' // melee — unchanged mechanic, existing clips, carried over from the old roaming archetype
  | 'telegraph2'
  | 'attack2'
  | 'swarm_wait' // SWARM CALL's adds are out; the fight pauses on the player until they're cleared
  | 'charge_windup'
  | 'charging'
  | 'slam'
  | 'stagger' // RED CHARGE's own punish window, distinct from the shared 'exposed' below
  | 'exposed' // punish window after melee or swarm-call
  | 'dead';

// --- tuning ---
// Same "first-pass estimate, no real playtest data yet" caveat as
// ai/boss.ts's own GRIDLOCK_MAX_HP comment. The Rat King's cycle spends a
// real chunk of wall-clock time on non-damage-window mechanics (the
// swarm-wait pause, the charge's travel time) that Gridlock's simpler
// two-attack loop doesn't have — for the SAME 45-100s M-4 target, that
// argues for somewhat LESS raw HP than Gridlock's 2800, not the same number
// copied over. Flag to Karan at the first real den playtest; do not treat
// this as validated.
export const RATKING_MAX_HP = 2200;

const ENGAGE_DELAY = 0.6;
const EXPOSED_DURATION = 2.2; // standard punish window — melee/swarm-call route here, same length Gridlock uses
// M-1's own wording: the charge "enters a long EXPOSED window" — longer than
// the standard one, the real reward for the player successfully getting a
// pillar (or anything else) between themselves and the charge.
const CHARGE_EXPOSED_DURATION = 3.5;
const HIT_FLASH_CLIP_DURATION = 0.333;
const DEATH_FADE_DURATION = 1.0;
const WHITE = new THREE.Color(0xffffff);
const GOLD = new THREE.Color(0xffc94a); // Classic's TOKENS.taxiGold — same reuse ai/boss.ts makes, for visual-language consistency across both bosses
// M-1(b)'s own wording: "RED CHARGE — glows red." The C08 brief is explicit
// this glow is NOT baked into the animation ("No color acting... a
// color-based telegraph would be invisible under the tint system") —
// it's this class's job, the same way GOLD is applied for EXPOSED. A review
// finding: the first cut of charge_windup/charging built the POSE half of
// this telegraph (the low battering-ram crouch, distinct from swarm call's
// tall rear-up) but missed the color half entirely.
const RED = new THREE.Color(0xff3b30);

// Measured from the delivered bos_ratking.glb (report.json: bbox dimensions
// 1.932657 x 2.3 x 1.700976) — same "round from the measured bbox" convention
// ai/boss.ts's own BOSS_RADIUS/BOSS_HEIGHT use for Gridlock.
export const RATKING_RADIUS = 1.0;
const RATKING_HEIGHT = 2.3;

// attack1 — melee bite/slam, a straight contact-range check (no AoE, no
// projectile). Range/damage carried forward UNCHANGED from the old roaming
// Enemy archetype's own values (ai/archetypes.ts's `rat_king` entry) — the
// migration moves which class hosts the FSM, not what the attack itself
// does; "melee up close (existing telegraph1/attack1, unchanged)" per the
// C08 brief.
export const MELEE_RANGE = 1.9;
export const MELEE_DAMAGE = 20;
const ATTACK1_IMPACT_T = 0.17; // ~40% into the delivered ~0.417s clip — an impact frame partway through, same reasoning as ai/boss.ts's own ATTACK1_IMPACT_T

// attack2 — SWARM CALL: "screech pose held, forepaws slam down once" (C08
// brief) — the forepaw slam is the actual release moment, later in the clip
// than a typical attack (the screech itself is the readable telegraph
// payoff; the slam-down is the mechanical trigger).
const ATTACK2_RELEASE_T = 0.55; // ~60% into the delivered ~0.958s clip
/** "Summons rats in waves" (M-1a) — a first pass at "a wave." Tune once this is playtested in a real den; not derived from any measured data (there is none yet). */
export const SWARM_RAT_COUNT = 3;
// Safety ceiling only — gated primarily on the manager reporting zero live
// adds (`addsAlive` param on `update()`), not this timer. If adds somehow
// never clear (a spawn failure, a stray unreachable rat), the fight
// shouldn't stay open forever. Same belt-and-suspenders style as
// ai/enemy.ts's diver `recoverDuration` floor.
const SWARM_WAIT_MAX = 20;

// RED CHARGE
/** Faster than the player's own base move speed — a charge that couldn't catch a standing-still player wouldn't be worth dodging. */
export const CHARGE_SPEED = 9;
/** A bit more generous than RATKING_RADIUS alone — the charge is a battering-ram lunge, meant to read as genuinely hard to sidestep at the last instant if you're standing in its path. */
export const CHARGE_HIT_RADIUS = 1.4;
export const CHARGE_DAMAGE = 26;
/** M-1's own wording: "wrong response = knockback + swarm pressure, not instant death" — a real, noticeable shove, not a token tap. */
export const CHARGE_KNOCKBACK = 7;
/** Close enough to the captured point to call the charge "arrived" even if nothing physically blocked it. */
const CHARGE_ARRIVE_RADIUS = 0.5;
/** Safety ceiling — the arena is finite, this should never actually trip in practice; here for the same reason SWARM_WAIT_MAX is. */
const CHARGE_MAX_DURATION = 2.5;

export interface RatKingVisual {
  group: THREE.Group;
  materials: THREE.Material[];
  mixer: THREE.AnimationMixer | null;
  clips: Map<string, THREE.AnimationClip> | null;
}

export interface RatKingTickResult {
  /** attack1's impact frame, player was in MELEE_RANGE — the manager should apply MELEE_DAMAGE + knockback to the player. */
  meleeFired: boolean;
  /** attack2's release frame — the manager should spawn SWARM_RAT_COUNT rats near the boss, then keep feeding their live count back via `update()`'s `addsAlive` parameter until they're cleared. */
  swarmCallFired: boolean;
  /** The charge's own contact check landed on the player this tick — the manager should apply CHARGE_DAMAGE/CHARGE_KNOCKBACK. */
  chargeHitPlayer: boolean;
  /** K-2 review fix: true only on the exact tick a charge's own `resolveHorizontal` call reported a genuine physical block (`resolved.blockedX || resolved.blockedZ`) as the reason it transitioned to 'slam' — NOT set for an "arrived at the frozen target" or safety-timeout stop. `world/dens.ts`'s `LairEncounter` must gate `reportChargeBroken()` on this being true (in addition to its own pillar-proximity check) — proximity alone can't distinguish "the charge actually hit something" from "the charge happened to end near a pillar it never touched." */
  chargeStoppedByWall: boolean;
}

export class RatKingBoss {
  readonly position = new THREE.Vector3();
  readonly health: Health;
  readonly group: THREE.Group;
  state: RatKingState = 'idle';

  private readonly visual: RatKingVisual;
  private readonly baseEmissive = new Map<THREE.MeshStandardMaterial, THREE.Color>();
  private stateT = 0;
  private deathT = 0;
  private hitFlashClipT = 0;
  private currentClip = '';
  private facing = 0;
  /** Fixed 3-cycle: melee -> swarm -> charge -> melee... Deterministic, matching Boss.ts's own "a fixed, learnable pattern is fairer" law (its `nextAttack` field) — three mechanics to learn instead of two, still fully learnable and harness-testable rather than randomized. */
  private nextOpener: 'melee' | 'swarm' | 'charge' = 'melee';
  private hasFiredThisAttack = false;
  /** §0.7 fire-time-capture: frozen the instant charge_windup resolves into charging, never re-derived from the player's live position during the dash itself — same discipline as ai/enemy.ts's diver `diveTarget`. */
  private readonly chargeTarget = new THREE.Vector3();
  /** PLAN-CITY3 §4 K-2 ("same L1 law" as Gridlock): melee only grants EXPOSED if it MISSED — set true the instant `updateAttack1` actually lands the hit (mirrors `result.meleeFired`), reset on entering attack1. */
  private meleeLanded = false;
  /** K-2: the charge only grants its long `stagger` punish window if it was stopped by a PILLAR (a genuine positive action) — set externally via `reportChargeBroken()` by `world/dens.ts`'s `LairEncounter`, which is the only thing with visibility into pillar positions (this class stays self-contained, matching `ai/boss.ts`'s own reasoning). Reset on entering charge_windup. */
  private chargeBroken = false;

  private readonly cachedAimTarget: AimTarget = { position: this.position, isTargetable: false };

  private readonly homeX: number;
  private readonly homeZ: number;

  constructor(visual: RatKingVisual, spawnX: number, spawnZ: number) {
    this.visual = visual;
    this.group = visual.group;
    this.health = createHealth(RATKING_MAX_HP, 0);
    this.homeX = spawnX;
    this.homeZ = spawnZ;
    this.position.set(spawnX, 0, spawnZ);
    for (const m of visual.materials) {
      if (m instanceof THREE.MeshStandardMaterial) this.baseEmissive.set(m, m.emissive.clone());
    }
    // Same cross-constant timing guard as ai/boss.ts's own constructor —
    // the byte-level validator checks clip presence/naming, not whether a
    // hardcoded impact-time assumption still fits a clip's real duration.
    const attack1Duration = visual.clips?.get('attack1')?.duration;
    if (attack1Duration !== undefined && attack1Duration <= ATTACK1_IMPACT_T) {
      console.warn(`[RatKingBoss] attack1 clip duration (${attack1Duration}s) is <= ATTACK1_IMPACT_T (${ATTACK1_IMPACT_T}s) — melee will never fire. Lower ATTACK1_IMPACT_T to match.`);
    }
    const attack2Duration = visual.clips?.get('attack2')?.duration;
    if (attack2Duration !== undefined && attack2Duration <= ATTACK2_RELEASE_T) {
      console.warn(`[RatKingBoss] attack2 clip duration (${attack2Duration}s) is <= ATTACK2_RELEASE_T (${ATTACK2_RELEASE_T}s) — the swarm call will never fire. Lower ATTACK2_RELEASE_T to match.`);
    }
    this.syncTransform();
    this.playClip('idle', true);
  }

  get aimTarget(): AimTarget {
    this.cachedAimTarget.isTargetable = this.state !== 'dead';
    return this.cachedAimTarget;
  }

  /** True during BOTH the standard post-melee/post-swarm punish window ('exposed') and the charge's own distinct 'stagger' window — both apply EXPOSED_DAMAGE_MULTIPLIER identically via `takeDamage`. */
  get isExposed(): boolean {
    return this.state === 'exposed' || this.state === 'stagger';
  }

  /**
   * Same contract as `Boss.engage()` (ai/boss.ts) — called once, the exact
   * tick a (future) `BossArena` transitions dormant->active, arming the
   * ENGAGE_DELAY beat before the first telegraph so the HP bar/intro toast
   * have a moment to register. A no-op if not currently `idle` — matches
   * Boss's own guard against a stray double-call or an arena activating
   * while a resumed/left-mid-fight boss is already mid-attack.
   */
  engage(): void {
    if (this.state === 'idle') this.stateT = ENGAGE_DELAY;
  }

  /** Restores a mid-fight retreat back to a fresh encounter — same contract as `Boss.resetEncounter()` (ai/boss.ts), including the opener resetting to the start of the 3-cycle so a reset fight always opens the same way. */
  resetEncounter(): void {
    this.health.hp = this.health.maxHp;
    this.health.invulnerableT = 0;
    this.health.flashT = 0;
    this.health.knockbackVel.set(0, 0, 0);
    this.state = 'idle';
    this.stateT = 0;
    this.hitFlashClipT = 0;
    this.nextOpener = 'melee';
    this.hasFiredThisAttack = false;
    this.meleeLanded = false;
    this.chargeBroken = false;
    this.currentClip = '';
    this.position.set(this.homeX, 0, this.homeZ);
    this.facing = 0;
    this.syncTransform();
    this.group.scale.setScalar(1);
    this.playClip('idle', true);
  }

  /** K-2 ("same L1 law" as ai/boss.ts): armored by default — CHIP_DAMAGE_MULTIPLIER outside EXPOSED, EXPOSED_DAMAGE_MULTIPLIER during it. Returns true if this hit was lethal. Routes through the shared `applyDamage` choke point (§0.7) — never mutates `.health.hp` itself. */
  takeDamage(amount: number, knockbackDir: THREE.Vector3, knockbackForce: number): boolean {
    if (this.state === 'dead') return false;
    const applied = this.isExposed ? amount * EXPOSED_DAMAGE_MULTIPLIER : amount * CHIP_DAMAGE_MULTIPLIER;
    const died = applyDamage(this.health, applied, { knockbackDir, knockbackForce });
    if (died) {
      this.state = 'dead';
      this.deathT = DEATH_FADE_DURATION;
      this.currentClip = '';
      this.playClip('death', false);
    } else if (this.state === 'idle' || this.state === 'swarm_wait' || this.isExposed) {
      // Same "cosmetic flinch only during safe windows, never mid-telegraph/
      // attack/charge" discipline as Boss.ts. 'swarm_wait' is included
      // alongside idle/exposed/stagger — the boss isn't attacking there
      // either, it's just waiting on the player to clear the adds.
      this.hitFlashClipT = HIT_FLASH_CLIP_DURATION;
      this.currentClip = '';
      this.playClip('hit', false);
    }
    return died;
  }

  private playClip(name: string, loop: boolean): void {
    if (!this.visual.mixer || !this.visual.clips) return;
    if (this.currentClip === name) return;
    const clip = this.visual.clips.get(name);
    if (!clip) return;
    this.visual.mixer.stopAllAction();
    const action = this.visual.mixer.clipAction(clip);
    action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.clampWhenFinished = !loop;
    action.reset().play();
    this.currentClip = name;
  }

  private clipDuration(name: string): number {
    return this.visual.clips?.get(name)?.duration ?? 0.65;
  }

  /** Same `encounterActive`-fed-every-tick contract as `Boss.update()` — a dormant boss just idles and never advances its own FSM until the (future) arena says the player has actually engaged. `addsAlive` defaults to 0 so every call site that doesn't care about swarm-call (every call site today — this class isn't live-wired yet) needs no changes. */
  update(dt: number, playerPos: THREE.Vector3, encounterActive: boolean, physics: BlockPhysics, addsAlive = 0): RatKingTickResult {
    const result: RatKingTickResult = { meleeFired: false, swarmCallFired: false, chargeHitPlayer: false, chargeStoppedByWall: false };
    tickHealth(this.health, dt);
    this.applyKnockbackMovement(dt, physics);
    this.visual.mixer?.update(dt);

    if (this.state === 'dead') {
      this.deathT -= dt;
      const t = Math.max(0, this.deathT / DEATH_FADE_DURATION);
      this.group.scale.setScalar(t);
      this.syncTransform();
      return result;
    }

    if (this.hitFlashClipT > 0) {
      this.hitFlashClipT -= dt;
      if (this.hitFlashClipT <= 0) this.currentClip = ''; // force the next playClip call to actually re-trigger, since currentClip still reads 'hit' otherwise
    }

    if (!encounterActive) {
      if (this.hitFlashClipT <= 0) this.playClip('idle', true);
      this.applyMechanicTint();
      this.applyHitFlashTint();
      this.syncTransform();
      return result;
    }

    this.facing = this.computeFacing(playerPos);

    switch (this.state) {
      case 'idle':
        this.updateIdle(dt);
        break;
      case 'telegraph1':
        this.updateOpenerTelegraph(dt, 1);
        break;
      case 'attack1':
        this.updateAttack1(dt, playerPos, result);
        break;
      case 'telegraph2':
        this.updateOpenerTelegraph(dt, 2);
        break;
      case 'attack2':
        this.updateAttack2(dt, result);
        break;
      case 'swarm_wait':
        this.updateSwarmWait(dt, addsAlive);
        break;
      case 'charge_windup':
        this.updateChargeWindup(dt, playerPos);
        break;
      case 'charging':
        this.updateCharging(dt, playerPos, physics, result);
        break;
      case 'slam':
        this.updateSlam(dt);
        break;
      case 'stagger':
        this.updateStagger(dt);
        break;
      case 'exposed':
        this.updateExposed(dt);
        break;
    }

    this.applyMechanicTint();
    this.applyHitFlashTint();
    this.syncTransform();
    return result;
  }

  private updateIdle(dt: number): void {
    if (this.hitFlashClipT <= 0) this.playClip('idle', true);
    this.stateT -= dt;
    if (this.stateT > 0) return;
    if (this.nextOpener === 'melee') {
      this.state = 'telegraph1';
      this.stateT = this.clipDuration('telegraph1');
      this.playClip('telegraph1', false);
    } else if (this.nextOpener === 'swarm') {
      this.state = 'telegraph2';
      this.stateT = this.clipDuration('telegraph2');
      this.playClip('telegraph2', false);
    } else {
      this.state = 'charge_windup';
      this.stateT = this.clipDuration('special_charge_windup');
      this.playClip('special_charge_windup', false);
    }
  }

  private updateOpenerTelegraph(dt: number, which: 1 | 2): void {
    this.stateT -= dt;
    if (this.stateT <= 0) {
      this.state = which === 1 ? 'attack1' : 'attack2';
      this.stateT = 0;
      this.hasFiredThisAttack = false;
      this.meleeLanded = false;
      this.playClip(which === 1 ? 'attack1' : 'attack2', false);
    }
  }

  /** K-2: melee earns EXPOSED only if it MISSED (`!meleeLanded`) — a landed hit is its own penalty ("wrong response = knockback + swarm pressure, not instant death" per M-1), not something that ALSO rewards the player. */
  private updateAttack1(dt: number, playerPos: THREE.Vector3, result: RatKingTickResult): void {
    this.stateT += dt;
    if (!this.hasFiredThisAttack && this.stateT >= ATTACK1_IMPACT_T) {
      this.hasFiredThisAttack = true;
      // §0.7 fire-time-capture: distance re-checked HERE, at the impact
      // frame, not at telegraph start — a player who backs out of range
      // during the wind-up avoids the hit.
      const dist = Math.hypot(playerPos.x - this.position.x, playerPos.z - this.position.z);
      if (dist <= MELEE_RANGE) {
        result.meleeFired = true;
        this.meleeLanded = true;
      }
    }
    if (this.stateT >= this.clipDuration('attack1')) {
      if (this.meleeLanded) this.returnToIdleUnbroken();
      else this.enterExposed(EXPOSED_DURATION);
    }
  }

  private updateAttack2(dt: number, result: RatKingTickResult): void {
    this.stateT += dt;
    if (!this.hasFiredThisAttack && this.stateT >= ATTACK2_RELEASE_T) {
      this.hasFiredThisAttack = true;
      result.swarmCallFired = true;
    }
    if (this.stateT >= this.clipDuration('attack2')) {
      this.state = 'swarm_wait';
      this.stateT = 0;
      if (this.hitFlashClipT <= 0) this.playClip('idle', true); // no dedicated "waiting on adds" clip was authored — idle's own loop while the fight pauses on the player
    }
  }

  /**
   * K-2 ("EXPOSED only when the adds died to the player, not the 20s
   * timeout"): "ignore them and they overwhelm, clear them and he's
   * exposed" (M-1a) is the reward condition — a genuine positive action
   * (clearing the adds) grants EXPOSED; SWARM_WAIT_MAX timing out with adds
   * STILL alive is the un-answered case and earns nothing, matching every
   * other L1 gate in this file/ai/boss.ts.
   */
  private updateSwarmWait(dt: number, addsAlive: number): void {
    if (this.hitFlashClipT <= 0) this.playClip('idle', true);
    this.stateT += dt;
    if (addsAlive <= 0) {
      this.enterExposed(EXPOSED_DURATION);
    } else if (this.stateT >= SWARM_WAIT_MAX) {
      this.returnToIdleUnbroken();
    }
  }

  private updateChargeWindup(dt: number, playerPos: THREE.Vector3): void {
    this.stateT -= dt;
    if (this.stateT <= 0) {
      this.state = 'charging';
      this.stateT = 0;
      this.hasFiredThisAttack = false;
      this.chargeBroken = false;
      this.chargeTarget.copy(playerPos); // §0.7 fire-time-capture — frozen here, never re-derived from the player's live position during the dash
      this.playClip('special_charge', true);
    }
  }

  /** K-2: called by `world/dens.ts`'s `LairEncounter` the SAME tick it determines the just-ended charge stopped against a pillar (right after 'charging'->'slam', well before 'slam''s own exit transition on a later tick) — mirrors `ai/boss.ts`'s `reportShockwaveBroken()` synchronous-report pattern. */
  reportChargeBroken(): void {
    this.chargeBroken = true;
  }

  /** K-2: a cycle that resolved un-broken (missed melee, timed-out swarm wait, or an un-pillared charge) gets NO reward — straight back to idle, opener still advances so the 3-cycle keeps rotating. Mirrors `ai/boss.ts`'s `returnToIdleUnbroken`. */
  private returnToIdleUnbroken(): void {
    this.state = 'idle';
    this.stateT = ENGAGE_DELAY;
    this.advanceOpener();
    if (this.hitFlashClipT <= 0) this.playClip('idle', true);
  }

  /**
   * The charge's own contact check and its termination are DELIBERATELY
   * independent of each other: whether the player got hit is decided purely
   * by proximity to the boss's current (moving) position, while the charge
   * ALWAYS ends in slam->stagger regardless of what stopped it — hitting a
   * wall/pillar (the "correct response": the player put something solid
   * between themselves and the boss's captured target, so the charge's own
   * travel gets cut short before it can reach them), arriving at the
   * captured point with nothing in the way, or the safety timeout. This is
   * why no line-of-sight pre-check is needed at windup-resolve time (unlike
   * ai/enemy.ts's diver, which cancels its whole dive on a broken sightline)
   * — the charge always commits and always resolves into a punish window;
   * only whether it ALSO catches the player along the way varies.
   */
  private updateCharging(dt: number, playerPos: THREE.Vector3, physics: BlockPhysics, result: RatKingTickResult): void {
    this.stateT += dt;
    const dx0 = this.chargeTarget.x - this.position.x;
    const dz0 = this.chargeTarget.z - this.position.z;
    const distToTarget = Math.hypot(dx0, dz0);
    const dirX = distToTarget > 1e-4 ? dx0 / distToTarget : 0;
    const dirZ = distToTarget > 1e-4 ? dz0 / distToTarget : 0;
    const dx = dirX * CHARGE_SPEED * dt;
    const dz = dirZ * CHARGE_SPEED * dt;
    const resolved = physics.resolveHorizontal(this.position.x, this.position.z, dx, dz, RATKING_RADIUS, this.position.y, this.position.y + RATKING_HEIGHT);
    this.position.x = resolved.x;
    this.position.z = resolved.z;

    if (!this.hasFiredThisAttack) {
      const distToPlayer = Math.hypot(playerPos.x - this.position.x, playerPos.z - this.position.z);
      if (distToPlayer <= CHARGE_HIT_RADIUS) {
        this.hasFiredThisAttack = true;
        result.chargeHitPlayer = true;
      }
    }

    if (resolved.blockedX || resolved.blockedZ || distToTarget <= CHARGE_ARRIVE_RADIUS || this.stateT >= CHARGE_MAX_DURATION) {
      // K-2 review fix: distinguish a GENUINE physical block from merely
      // arriving at the frozen target or timing out — only the former is a
      // real "the player put something solid in the way" positive action.
      // `world/dens.ts`'s pillar-proximity check alone can't tell "stopped
      // because it hit something" from "stopped because it happened to end
      // up near a pillar it never touched" (an arrival/timeout can land
      // anywhere, including within the pillar's own proximity halo).
      if (resolved.blockedX || resolved.blockedZ) result.chargeStoppedByWall = true;
      this.state = 'slam';
      this.stateT = 0;
      this.playClip('special_slam', false);
    }
  }

  /** K-2: the long `stagger` punish window only follows a PILLAR-broken charge (`chargeBroken`, set externally via `reportChargeBroken()`) — a charge that hit the player or simply timed out in the open earns nothing, matching M-1's own "wrong response = knockback + swarm pressure, not instant death" (the knockback/pressure IS the consequence; no bonus punish window on top of it). */
  private updateSlam(dt: number): void {
    this.stateT += dt;
    if (this.stateT >= this.clipDuration('special_slam')) {
      if (this.chargeBroken) {
        this.state = 'stagger';
        this.stateT = CHARGE_EXPOSED_DURATION;
        // C08's own NOTES.md: special_slam's final pose and special_stagger's
        // first pose are numerically identical, authored so this cut reads as
        // one continuous motion — stagger has never played yet this fight
        // cycle, so playClip's own currentClip-dedup guard won't skip it.
        this.playClip('special_stagger', true);
      } else {
        this.returnToIdleUnbroken();
      }
    }
  }

  private updateStagger(dt: number): void {
    if (this.hitFlashClipT <= 0) this.playClip('special_stagger', true);
    this.stateT -= dt;
    if (this.stateT <= 0) {
      this.state = 'idle';
      this.stateT = ENGAGE_DELAY;
      this.advanceOpener();
    }
  }

  private enterExposed(duration: number): void {
    this.state = 'exposed';
    this.stateT = duration;
    // No dedicated post-melee/post-swarm "exposed" clip was authored (only
    // the charge's own special_stagger) — idle's own loop plays underneath
    // the gold tint, same idiom ai/boss.ts (Gridlock) uses.
    if (this.hitFlashClipT <= 0) this.playClip('idle', true);
  }

  private updateExposed(dt: number): void {
    if (this.hitFlashClipT <= 0) this.playClip('idle', true);
    this.stateT -= dt;
    if (this.stateT <= 0) {
      this.state = 'idle';
      this.stateT = ENGAGE_DELAY;
      this.advanceOpener();
    }
  }

  private advanceOpener(): void {
    this.nextOpener = this.nextOpener === 'melee' ? 'swarm' : this.nextOpener === 'swarm' ? 'charge' : 'melee';
  }

  private computeFacing(playerPos: THREE.Vector3): number {
    const dx = playerPos.x - this.position.x;
    const dz = playerPos.z - this.position.z;
    const len = Math.hypot(dx, dz);
    return len > 1e-4 ? Math.atan2(dx / len, dz / len) : this.facing;
  }

  /**
   * The three mutually-exclusive mechanic tints: RED during the charge's own
   * telegraph+dash (M-1b — a threat cue that stays on for the whole time the
   * charge is dangerous, not just its windup), GOLD during the standard
   * exposed/stagger punish window (same idiom `Boss.ts` uses for Gridlock),
   * or neither. `lerp(base, color, 0.6)` at factor 0 is NOT used here (unlike
   * the old single-tint version) since there are now three cases, not a
   * simple on/off — `color === null` explicitly restores the base emissive
   * instead.
   */
  private applyMechanicTint(): void {
    if (this.health.flashT > 0) return; // the white hit-flash (applyHitFlashTint) always wins over either mechanic tint
    const color = this.state === 'charge_windup' || this.state === 'charging' ? RED : this.isExposed ? GOLD : null;
    for (const [m, base] of this.baseEmissive) {
      if (color) m.emissive.copy(base).lerp(color, 0.6);
      else m.emissive.copy(base);
    }
  }

  private applyHitFlashTint(): void {
    if (this.health.flashT <= 0) return;
    const lit = this.health.flashT / FLASH_DURATION;
    for (const [m, base] of this.baseEmissive) {
      m.emissive.copy(base).lerp(WHITE, lit);
    }
  }

  private applyKnockbackMovement(dt: number, physics: BlockPhysics): void {
    if (this.health.knockbackVel.lengthSq() < 1e-4) return;
    const dx = this.health.knockbackVel.x * dt;
    const dz = this.health.knockbackVel.z * dt;
    const resolved = physics.resolveHorizontal(this.position.x, this.position.z, dx, dz, RATKING_RADIUS, this.position.y, this.position.y + RATKING_HEIGHT);
    this.position.x = resolved.x;
    this.position.z = resolved.z;
  }

  private syncTransform(): void {
    this.group.position.set(this.position.x, this.position.y, this.position.z);
    // R-1 (PLAN-CITY3 §0 L2): bos_ratking.glb is +X-forward (nose X
    // [0.40,0.91], per city/CITY3-FINDINGS.md) — without this offset the
    // Rat King charged/telegraphed 90° sideways, an unreadable tell that
    // directly breaks "boss difficulty = knowledge, not reflexes." See
    // core/assets.ts's CHARACTER_FORWARD_YAW_OFFSET for the derivation.
    this.group.rotation.y = this.facing + CHARACTER_FORWARD_YAW_OFFSET;
  }
}
